import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder } from '@angular/forms';
import { ApiService } from 'projects/admin/src/app/services/api.service'
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core'
import { EmployeeModel } from 'projects/core/src/lib/models/employee.model'
import { TranslocoService } from '@ngneat/transloco';
import { UiService } from '../../../services/ui.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { forkJoin, Observable } from 'rxjs';
import { TextblockModel } from 'projects/core/src/lib/models/textblock.model';
import { TextContentModes } from '../content-editor/content-editor.component';
import { TextcontentModel } from 'projects/core/src/lib/models/textcontent.model';

interface VisibleElement {
  identifier: string;
  preview: string;
}

@Component({
  selector: 'app-placeholders',
  templateUrl: './placeholders.component.html',
  styleUrls: ['./placeholders.component.scss']
})
export class PlaceholdersComponent implements OnInit, OnChanges {

  @Input() edit = false;
  @Input() mode: TextContentModes;
  @Input() context: string = null;
  @Input() language: string = null;
  @Input() template: string = null;

  @ViewChild('promptFormRef') promptFormRef: ElementRef<any>;

  public loading = true;
  public placeholders: any = null;
  public visiblePlaceholders: Array<VisibleElement> = [];
  public textblocks: Array<TextblockModel> = [];
  public visibleTextblocks: Array<VisibleElement> = [];
  public TextContentModes = TextContentModes;

  public promptVisible = false;
  public promptTempValue: string = null;
  public promptForm: UntypedFormGroup = this.fb.group({
    link: new UntypedFormControl('')
  });

  @Output() select = new EventEmitter<EmployeeModel>();

  constructor(
    private api: ApiService,
    private transloco: TranslocoService,
    private ui: UiService,
    private clipboard: Clipboard,
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    const requests: Array<Observable<any>> = [
      this.api.getModels('placeholders', (this.template !== null) ? { template: this.template } : {})
    ];
    if (this.mode === TextContentModes.Texttemplate) {
      requests.push(this.api.getModels('textblocks'));
    }
    forkJoin(requests).subscribe({
      next: (result: any) => {
        if (result[0].data) {
          this.placeholders = result[0].data;
        }
        if (result[1]?.data) {
          this.textblocks = result[1].data;
        }
        this.setVisiblePlaceholders();
        this.setVisibleTextblocks();
      },
      error: (error: any) => {
      }
    }).add(() => {
      this.loading = false;
    });
  }

  ngOnChanges(): void {
    this.setVisiblePlaceholders();
    this.setVisibleTextblocks();
  }

  public onPlaceholderClick(placeholder: string): void {
    if (this.edit === true) {
      if (placeholder.match(new RegExp('^%WEBLINK_'))) {
        this.promptVisible = true;
        this.promptTempValue = placeholder;
        this.promptFormRef.nativeElement.querySelector('input').focus(); // ???
      } else {
        this.copyToClipboard(placeholder);
      }
    }
  }

  public onPlaceholderLinkInput(): void {
    const formValue = this.promptForm.get('link').value;
    let value = this.promptTempValue;
    if (formValue) {
      value = value.replace(new RegExp('%$'), '|' + formValue + '%')
    }
    this.copyToClipboard(value);
    this.promptForm.get('link').setValue('');
    this.promptVisible = false;
  }

  protected copyToClipboard(value: string): void {
    this.clipboard.copy(value);
    this.ui.toastSuccess(value + ' ' + this.transloco.translate('common.copied_to_clipboard'));
  }

  protected setVisiblePlaceholders(): void {
    this.visiblePlaceholders = [];
    const models: Array<string> = [];
    if (this.context !== null && this.placeholders?.dependencies[this.context]?.length > 0) {
      this.placeholders.dependencies[this.context].forEach((model: string) => {
        this.visiblePlaceholders = [...this.visiblePlaceholders, ... this.getPlaceholders(model)];
      });
    }
  }

  protected getPlaceholders(model: string): Array<VisibleElement> {
    const placeholders: Array<VisibleElement> = [];
    if (this.placeholders?.placeholders[model]?.length > 0) {
      this.placeholders.placeholders[model].forEach((placeholder: string) => {
        const value = placeholder.replace(/^[A-Z]+_/, '').toLowerCase();
        placeholders.push({
          identifier: placeholder,
          preview: (this.placeholders?.preview_data[model] && this.placeholders?.preview_data[model][value]) ? this.placeholders?.preview_data[model][value] : null
        });
      });
    }
    return placeholders.sort((a, b) => a.identifier.localeCompare(b.identifier));
  }

  protected setVisibleTextblocks(): void {
    this.visibleTextblocks = [];
    if (this.language !== null && this.textblocks.length > 0) {
      this.textblocks.forEach((textblock: TextblockModel) => {
        const content = textblock.contents.find((content: TextcontentModel) => {
          return content.language?.key === this.language;
        });
        if (content) {
          this.visibleTextblocks.push({
            identifier: textblock.identifier,
            preview: content.content.replace(/(<([^>]+)>)/gi, '')
          });
        }
      });
    }
  }

}
